'use client';

import { FC, Fragment, lazy, useState, Suspense } from 'react';

import { AssetUtils } from '@ravago/shared/next-data-access';
import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { NormalizedProduct } from '@ravago/shared/page-data/models/contentful-mapped';
import { AspectRatio } from '@ravago/shared/radiance/components/aspect-ratio/components/AspectRatio/AspectRatio';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Flyout } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { FlyoutContent } from '@ravago/shared/radiance/components/flyout/components/Flyout/FlyoutContent';
import { FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/FlyoutHeader';
import { Image } from '@ravago/shared/radiance/components/image/components/Image/Image';
import { List } from '@ravago/shared/radiance/components/list/components/List/List';
import { ListItem } from '@ravago/shared/radiance/components/list/components/ListItem/ListItem';
import { Menu } from '@ravago/shared/radiance/components/menu/components/Menu/Menu';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableHead } from '@ravago/shared/radiance/components/table/components/TableHead/TableHead';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { OrderBaseUrls, ShipmentOrder } from 'libs/shared/page-data/src/lib/models/elements/order.model';

import { MockLink } from '../../mock-link/mock-link';
import { SystemPageLink } from '../../system-page-link/system-page-link';

const OrderLineFlyout = lazy(() => import('../../../shared/order-line-flyout/order-line-flyout').then((m) => ({ default: m.OrderLineFlyout })));

interface Props {
   locale: string;
   isConsumer: boolean;
   acceptWebp?: boolean;
   assetBasePath: string;
   orders: ShipmentOrder[];
   orderBaseUrls: OrderBaseUrls;
   showInvoiceDocuments: boolean;
   isPreview?: boolean;
   requestCoa: (orderNumber: string, producerLotNr: string) => Promise<boolean>;
   requestDeliveryNote: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   requestBol: (orderNumber: string, transportOrderLineNr: number, bundledTransportOrderId?: number) => Promise<boolean>;
   downloadDocument: (downloadEndpoint: string, correlationId: string) => Promise<string | undefined>;
   isUSBusinessContext: boolean;
}

export const ShipmentProducts: FC<Props> = ({
   locale,
   orders,
   isConsumer,
   acceptWebp,
   assetBasePath,
   isPreview,
   orderBaseUrls,
   showInvoiceDocuments,
   requestDeliveryNote,
   requestBol,
   requestCoa,
   downloadDocument,
   isUSBusinessContext
}) => {
   const translate = useTranslations();
   const format = useFormatter();
   const { desktop, tabLand } = useBreakpoint();

   const [menuOpenIndex, setMenuOpenIndex] = useState<number | undefined>();
   const [flyoutOpen, setFlyoutOpen] = useState(false);
   const [orderNumber, setOrderNumber] = useState<string | null>(null);
   const [contentfulProduct, setContentfulProduct] = useState<NormalizedProduct | undefined>(undefined);

   const onMenuToggle = (index: number) => setMenuOpenIndex((curr) => (curr !== undefined ? undefined : index));

   const onMenuClose = () => setMenuOpenIndex(undefined);

   const onOrderLineButtonClick = async (orderLine: ShipmentOrder) => {
      setMenuOpenIndex(undefined);

      if (isConsumer) {
         setOrderNumber(orderLine.orderNumber);
         setContentfulProduct(orderLine.contentfulProduct);
         setFlyoutOpen(true);
      }
   };

   return (
      <>
         <Box>
            <Typography variant="heading-4" component="h4" color="strong">
               {translate('shipment.products.products')}
            </Typography>
         </Box>
         {(desktop || tabLand) && !isPreview ? (
            <Table rounded border hover={false}>
               <TableHead>
                  <TableRow>
                     <TableHeader> {translate('shipment.products.product')}</TableHeader>
                     <TableHeader> {translate('shipment.products.order-line-number')}</TableHeader>
                     <TableHeader> {translate('shipment.products.quantity')}</TableHeader>
                     <TableHeader> {translate('shipment.products.packaging')}</TableHeader>
                     <TableHeader>{}</TableHeader>
                  </TableRow>
               </TableHead>
               <TableBody>
                  {orders.map((order, index) => (
                     <TableRow key={order.orderNumber} onClick={() => {}}>
                        <TableCell onClick={() => onOrderLineButtonClick(order)}>
                           <Box>
                              <AspectRatio aspectRatio="1:1">
                                 <Image
                                    src={
                                       order.contentfulProduct?.brand?.producer?.logo?.url || `${assetBasePath}/images/placeholders/default-logo.png`
                                    }
                                    alt={order.product.description}
                                    title={order.product.description}
                                    height="52px"
                                    width="52px"
                                    objectFit="contain"
                                 />
                              </AspectRatio>
                              <Box direction="column" gap="none" justify="center">
                                 <Typography component="span" variant="body-highlight">
                                    {order.product.description}
                                 </Typography>
                                 {order.customerOrderReference && (
                                    <Typography component="span" variant="small" color="subtle">
                                       {translate('shipment.order-reference')} {order.customerOrderReference}
                                    </Typography>
                                 )}
                              </Box>
                           </Box>
                        </TableCell>
                        <TableCell onClick={() => onOrderLineButtonClick(order)}>{order.orderNumber}</TableCell>
                        <TableCell onClick={() => onOrderLineButtonClick(order)}>{format.quantity(order.quantity)}</TableCell>
                        <TableCell onClick={() => onOrderLineButtonClick(order)}>{order.packaging}</TableCell>
                        <TableCell width="60px">
                           <Menu
                              open={menuOpenIndex === index}
                              closeOnOutsideClick
                              align="right"
                              onMenuClose={() => onMenuClose()}
                              content={
                                 <List>
                                    <ListItem onClick={() => onOrderLineButtonClick(order)}>{translate('shipment.table.show-more')}</ListItem>
                                    {isConsumer ? (
                                       <>
                                          <SystemPageLink
                                             baseRoute={orderBaseUrls.orderDetailBaseUrl ?? undefined}
                                             type={SystemPageType.ORDER_DETAIL}
                                             locale={locale}
                                             slug={order.orderNumber.split('-')[0]}
                                          >
                                             <ListItem>{translate('shipment.table.order-details')}</ListItem>
                                          </SystemPageLink>
                                          {order.agreementNumber !== undefined && (
                                             <SystemPageLink
                                                baseRoute={orderBaseUrls.callOffAgreementBaseUrl ?? undefined}
                                                type={SystemPageType.CALL_OFF_ORDER_DETAIL}
                                                locale={locale}
                                                slug={order.agreementNumber}
                                             >
                                                <ListItem>{translate('shipment.table.call-off-agreement')}</ListItem>
                                             </SystemPageLink>
                                          )}
                                          <SystemPageLink
                                             baseRoute={orderBaseUrls.myProductsAndPricesBaseUrl ?? undefined}
                                             type={SystemPageType.MY_PRODUCTS_AND_PRICES}
                                             locale={locale}
                                          >
                                             <ListItem>{translate('shipment.products.new-quote')}</ListItem>
                                          </SystemPageLink>
                                       </>
                                    ) : (
                                       <>
                                          <MockLink>
                                             <ListItem>{translate('shipment.table.order-details')}</ListItem>
                                          </MockLink>
                                          <MockLink>
                                             <ListItem>{translate('shipment.products.new-quote')}</ListItem>
                                          </MockLink>
                                       </>
                                    )}
                                 </List>
                              }
                           >
                              <Box content="end" items="end">
                                 <IconButton
                                    icon="ellipsis-vertical"
                                    type="button"
                                    variant="on-light"
                                    ariaLabel="open"
                                    onClick={() => onMenuToggle(index)}
                                 />
                              </Box>
                           </Menu>
                        </TableCell>
                     </TableRow>
                  ))}
               </TableBody>
            </Table>
         ) : (
            <Table rowDividers={false} rounded border tableLayout="auto">
               <TableBody>
                  {orders.map((order, index) => (
                     <Fragment key={order.orderNumber}>
                        <TableRow>
                           <TableCell colSpan={2} onClick={() => onOrderLineButtonClick(order)}>
                              <Box spacing={{ top: 'md' }}>
                                 <AspectRatio aspectRatio="1:1">
                                    <Image
                                       src={
                                          order.contentfulProduct?.brand?.producer?.logo?.url
                                             ? AssetUtils.optimizeImageIfRequired(order.contentfulProduct.brand.producer.logo.url, acceptWebp)
                                             : `${assetBasePath}/images/placeholders/default-logo.png`
                                       }
                                       alt={order.product.description}
                                       title={order.product.description}
                                       height="52px"
                                       width="52px"
                                       objectFit="contain"
                                    />
                                 </AspectRatio>
                                 <Box direction="column" gap="none">
                                    {order.customerOrderReference && (
                                       <Typography component="span" variant="body" color="subtle">
                                          {order.customerOrderReference}
                                       </Typography>
                                    )}
                                    <Typography component="span" variant="body-highlight">
                                       {order.product.description}
                                    </Typography>
                                 </Box>
                              </Box>
                           </TableCell>
                           <TableCell width="60px">
                              <Menu
                                 open={menuOpenIndex === index}
                                 closeOnOutsideClick
                                 align="right"
                                 onMenuClose={() => onMenuClose()}
                                 content={
                                    <List>
                                       <ListItem onClick={() => onOrderLineButtonClick(order)}>{translate('shipment.table.show-more')}</ListItem>
                                       {isConsumer ? (
                                          <>
                                             <SystemPageLink
                                                baseRoute={orderBaseUrls.orderDetailBaseUrl ?? undefined}
                                                type={SystemPageType.ORDER_DETAIL}
                                                locale={locale}
                                                slug={order.orderNumber.split('-')[0]}
                                             >
                                                <ListItem>{translate('shipment.table.order-details')}</ListItem>
                                             </SystemPageLink>
                                             {order.agreementNumber !== undefined && (
                                                <SystemPageLink
                                                   baseRoute={orderBaseUrls.callOffAgreementBaseUrl ?? undefined}
                                                   type={SystemPageType.CALL_OFF_ORDER_DETAIL}
                                                   locale={locale}
                                                   slug={order.agreementNumber}
                                                >
                                                   <ListItem>{translate('shipment.table.call-off-agreement')}</ListItem>
                                                </SystemPageLink>
                                             )}
                                             <SystemPageLink
                                                baseRoute={orderBaseUrls.myProductsAndPricesBaseUrl ?? undefined}
                                                type={SystemPageType.MY_PRODUCTS_AND_PRICES}
                                                locale={locale}
                                             >
                                                <ListItem>{translate('shipment.products.new-quote')}</ListItem>
                                             </SystemPageLink>
                                          </>
                                       ) : (
                                          <>
                                             <MockLink>
                                                <ListItem>{translate('shipment.table.order-details')}</ListItem>
                                             </MockLink>
                                             <MockLink>
                                                <ListItem>{translate('shipment.products.new-quote')}</ListItem>
                                             </MockLink>
                                          </>
                                       )}
                                    </List>
                                 }
                              >
                                 <Box content="end" items="end">
                                    <IconButton
                                       icon="ellipsis-vertical"
                                       type="button"
                                       variant="on-light"
                                       ariaLabel="open"
                                       onClick={() => onMenuToggle(index)}
                                    />
                                 </Box>
                              </Menu>
                           </TableCell>
                        </TableRow>
                        <TableRow onClick={() => onOrderLineButtonClick(order)}>
                           <TableCell width="120px">
                              <Typography variant="body" color="subtle" component="span">
                                 {translate('shipment.products.order-line-number')}
                              </Typography>
                           </TableCell>
                           <TableCell>{order.customerProductReference}</TableCell>
                        </TableRow>
                        <TableRow onClick={() => onOrderLineButtonClick(order)}>
                           <TableCell width="120px">
                              <Typography variant="body" color="subtle" component="span">
                                 {translate('shipment.products.quantity')}
                              </Typography>
                           </TableCell>
                           <TableCell>{format.quantity(order.quantity)}</TableCell>
                        </TableRow>
                        <TableRow divider onClick={() => onOrderLineButtonClick(order)}>
                           <TableCell width="120px">
                              <Typography variant="body" color="subtle" component="span">
                                 {translate('shipment.products.packaging')}
                              </Typography>
                           </TableCell>
                           <TableCell>{order.packaging}</TableCell>
                        </TableRow>
                     </Fragment>
                  ))}
               </TableBody>
            </Table>
         )}
         {isConsumer && orderNumber && (
            <Flyout open={flyoutOpen} closeOnOutsideClick onFlyoutClose={() => setFlyoutOpen(false)}>
               <FlyoutHeader>
                  {translate('page-consumer.order-line-flyout.order')} {orderNumber}
               </FlyoutHeader>
               <FlyoutContent>
                  <Suspense>
                     <OrderLineFlyout
                        locale={locale}
                        isUSBusinessContext={isUSBusinessContext}
                        fullOrderNumber={orderNumber}
                        contentfulProduct={contentfulProduct}
                        openedFrom="shipment_detail"
                        orderBaseUrls={orderBaseUrls}
                        showInvoiceDocuments={showInvoiceDocuments}
                        requestCoa={requestCoa}
                        requestBol={requestBol}
                        requestDeliveryNote={requestDeliveryNote}
                        downloadDocument={downloadDocument}
                     />
                  </Suspense>
               </FlyoutContent>
            </Flyout>
         )}
      </>
   );
};
